import { Button, Clickable, Col, Input, Text } from 'components';
import { Api, theme } from 'lib';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { store } from 'store';
import styled from 'styled-components';

const ListItem = styled.li`
  color: ${theme.GREY_60};
  margin: 8px 0 0;
`;

const ResetPassword = ({ code }) => {
  const [newPassword1, setNewPassword1] = useState(''),
    [newPassword2, setNewPassword2] = useState(''),
    [errs, setErrs] = useState([]),
    user = useSelector((state) => state.user),
    { isMobile } = useSelector((state) => state.appState),
    navigate = useNavigate(),
    buttonDisabled = newPassword1.length < 8 || newPassword2.length < 8,
    { t } = useTranslation();

  async function changePassword() {
    if (newPassword1 !== newPassword2) {
      setErrs([
        { input: 'newPassword1', err: t('forgotPassword.doesntMatch') },
        { input: 'newPassword2', err: t('forgotPassword.doesntMatch') }
      ]);

      return;
    }
    /**
     * @property {string} err
     * @property {string} redirect
     */
    const res = await Api.post(
      '/users/changePasswordWithCode',
      { code, newPassword: newPassword1 },
      'changePassword'
    );

    if (!res) {
      return;
    }

    if (Array.isArray(res)) {
      return setErrs(res);
    }

    if (res.err) {
      if (res.input) {
        return setErrs([res]);
      }

      store.dispatch({
        type: 'HANDLE_RES',
        payload: { err: res.err }
      });
    }

    setNewPassword1('');
    setNewPassword2('');
    setErrs([]);
    navigate(res.redirect || '/login');
  }

  return (
    <Col noFlex center marg="36px 0 0">
      <Text h1 center>
        {t('forgotPassword.createNewPassword')}
      </Text>

      <Col wid="296px" center marg="32px 0 0">
        <Input
          value={newPassword1}
          onChange={(e) => {
            setNewPassword1(e.target.value);
            setErrs(errs.filter((x) => x.input !== 'newPassword1'));
          }}
          placeholder={t('auth.newPassword')}
          password
          error={errs.find((x) => x.input === 'newPassword1')?.err}
        />

        <Input
          marg="28px 0 0"
          value={newPassword2}
          onChange={(e) => {
            setNewPassword2(e.target.value);
            setErrs(errs.filter((x) => x.input !== 'newPassword2'));
          }}
          /*
            The second field is the confirmation, and it said "New password" too.

            Two identical placeholders, one above the other, with nothing on the screen to say the
            lower one is a repeat — and both are masked, so there is no typed text to tell them
            apart either. The key has always existed and has never been used by anything, which is
            why the error message underneath ("Doesn't match") was the first time the screen
            admitted there were two different questions.
          */
          placeholder={t('auth.confirmPassword')}
          password
          error={errs.find((x) => x.input === 'newPassword2')?.err}
        />

        <Button
          disabled={buttonDisabled}
          onClick={changePassword}
          action="changePassword"
          text={t('auth.resetPassword')}
          marg="28px 0 0"
        />
      </Col>

      {/*
        The rules, at a width the phone actually has.

        `456px` is wider than a 375px phone, so the list overflowed the viewport: the lines ran off
        the right edge and took a horizontal scrollbar with them. Every other screen in this flow
        already asks `isMobile` for exactly this — `EnterEmail` sizes its instruction line the same
        way — and this one never did, because it is the only step reached by a link from an email
        rather than by tapping through the flow.
      */}
      <Col wid={isMobile ? 'calc(100vw - 32px)' : '456px'} lineHt="20px" marg="10px 0 0">
        <ul>
          <ListItem>{t('forgotPassword.passwordRule1')}</ListItem>
          <ListItem>{t('forgotPassword.passwordRule2')}</ListItem>
          <ListItem>{t('forgotPassword.passwordRule3')}</ListItem>
          <ListItem>{t('forgotPassword.passwordRule4')}</ListItem>
          <ListItem>{t('forgotPassword.passwordRule5')}</ListItem>
        </ul>
      </Col>

      <Clickable
        onClick={() => {
          navigate(user?._id ? '/settings' : '/login');
        }}
        marg="27px 0 0"
      >
        <Text b2 center bold>
          {t(user?._id ? 'forgotPassword.backToSettings' : 'forgotPassword.backToLogin')}
        </Text>
      </Clickable>
    </Col>
  );
};

export default ResetPassword;
